import { DEFAULT_CURRENCY } from './constants.js';

/** Format minor-unit integer as display currency — DP-02; never float math on amounts */
export function formatMoneyMinor(amountMinor: number, currency: string = DEFAULT_CURRENCY): string {
  if (amountMinor === 0) {
    return 'Free';
  }
  const formatted = formatCurrencyMajor(Math.abs(amountMinor), currency);
  return amountMinor < 0 ? `−${formatted}` : formatted;
}

function formatCurrencyMajor(amountMinorAbs: number, currency: string): string {
  const pounds = Math.trunc(amountMinorAbs / 100);
  const pence = amountMinorAbs % 100;
  const major = pounds + pence / 100;
  return new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(major);
}

/** Estimated range — £x – £y (estimated) */
export function formatMoneyRange(
  amountMinorMin: number,
  amountMinorMax: number,
  currency: string = DEFAULT_CURRENCY,
): string {
  return `${formatCurrencyMajor(amountMinorMin, currency)} – ${formatCurrencyMajor(amountMinorMax, currency)} (estimated)`;
}

/** DP-02: zero-value discount lines are not rendered */
export function shouldRenderMoneyLine(
  amountMinor: number,
  options?: { suppressZero?: boolean },
): boolean {
  if (options?.suppressZero && amountMinor === 0) {
    return false;
  }
  return true;
}

export function moneyAriaLabel(amountMinor: number, currency: string = DEFAULT_CURRENCY): string {
  if (amountMinor === 0) {
    return 'Free';
  }
  const absMinor = Math.abs(amountMinor);
  const pounds = Math.trunc(absMinor / 100);
  const pence = absMinor % 100;
  const sign = amountMinor < 0 ? 'Credit ' : '';
  return `${sign}${pounds} pounds and ${pence} pence ${currency}`;
}

/** Parse display string to minor units using integer arithmetic */
export function parseMoneyToMinor(display: string): number {
  const negative = display.includes('−') || display.trimStart().startsWith('-');
  const digits = display.replace(/[^0-9.]/g, '');
  const [whole = '0', frac = ''] = digits.split('.');
  const pence = Number(whole) * 100 + Number(frac.padEnd(2, '0').slice(0, 2));
  return negative ? -pence : pence;
}
